import React, { useEffect, useState } from 'react';
import { StatusData, SystemStatus, Incident, IncidentUpdate } from './types';
import { fetchStatusData } from './api';

const StatusIndicator: React.FC<{ status: SystemStatus['status'] }> = ({ status }) => {
  const colors = {
    healthy: '#22c55e',
    degraded: '#eab308',
    failed: '#ef4444',
  };

  return (
    <div
      className="w-3 h-3 rounded-full inline-block mr-2"
      style={{ backgroundColor: colors[status] }}
    />
  );
};

export const StatusDashboard: React.FC = () => {
  const [statusData, setStatusData] = useState<StatusData | null>(null);
  const [error, setError] = useState<string | null>(null);


  useEffect(() => {
    const loadStatusData = async () => {
      try {
        const data = await fetchStatusData();
        setStatusData(data);
        setError(null);
      } catch (err) {
        setError('Failed to load status data. Please try again later.');
        console.error('Error loading status data:', err);
      }
    };

    loadStatusData();
  }, []);

  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'long',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto py-12 px-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Accorded System Status</h1>
          <div className="text-sm text-gray-500">
            Last Updated: {new Date(statusData?.lastUpdated || new Date().toISOString()).toLocaleString(undefined, {
              dateStyle: 'medium',
              timeStyle: 'medium',
            })}
          </div>
        </div>
        
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Scheduled Maintenance</h2>
          {error ? (
            <div className="bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          ) : !statusData ? (
            <div className="flex justify-center items-center h-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
            </div>
          ) : statusData.maintenance.startTime ? (
            <div className="text-gray-600">
              <p className="mb-1">
                {formatDate(statusData.maintenance.startTime)} - {formatDate(statusData.maintenance.endTime!)}
              </p>
              {statusData.maintenance.message && (
                <p className="text-sm text-gray-500">{statusData.maintenance.message}</p>
              )}
            </div>
          ) : (
            <p className="text-gray-600">None Scheduled</p>
          )}
        </div>
        
        <div className="bg-white shadow rounded-lg">
          {error ? (
            <div className="bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          ) : !statusData ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : statusData.categories.map((category) => (
            <div key={category.name} className="p-6 border-b border-gray-200 last:border-b-0">
              <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">
                {category.name}
              </h2>
              <div className="space-y-4 bg-gray-50 rounded-lg p-4">
                {category.systems.map((system) => (
                  <div
                    key={system.name}
                    className="flex items-center justify-between bg-white rounded-lg p-3 shadow-sm"
                  >
                    <div className="flex items-center">
                      <StatusIndicator status={system.status} />
                      <span className="font-medium text-gray-900">{system.name}</span>
                    </div>
                    {system.message && (
                      <span className="text-sm text-gray-500">
                        {system.message}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

          <div className="mt-8 p-6 bg-white shadow rounded-lg">
            <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">
              Incident History
              <span className="text-sm font-normal text-gray-500 ml-2">
                (Past month)
              </span>
            </h2>
            {error ? (
              <div className="bg-red-50 border-l-4 border-red-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            ) : !statusData ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : statusData.incidentHistory.incidents.length > 0 ? (
              <div className="space-y-6">
                {statusData.incidentHistory.incidents.map((incident: Incident) => (
                  <div key={incident.id} className="border-l-4 border-gray-200 pl-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{incident.title}</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        incident.severity === 'critical' ? 'bg-red-100 text-red-800' :
                        incident.severity === 'major' ? 'bg-orange-100 text-orange-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {incident.severity.charAt(0).toUpperCase() + incident.severity.slice(1)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 mb-2">
                      {formatDate(incident.startTime)} - {incident.endTime ? formatDate(incident.endTime) : 'Ongoing'}
                    </div>
                    <div className="text-sm text-gray-600 mb-2">
                      Affected Systems: {incident.affectedSystems.join(', ')}
                    </div>
                    <div className="space-y-2">
                      {incident.updates.map((update: IncidentUpdate, index: number) => (
                        <div key={index} className="text-sm">
                          <span className="text-gray-500">{formatDate(update.timestamp)}</span>
                          <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                            update.status === 'resolved' ? 'bg-green-100 text-green-800' :
                            update.status === 'monitoring' ? 'bg-blue-100 text-blue-800' :
                            update.status === 'identified' ? 'bg-purple-100 text-purple-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {update.status.charAt(0).toUpperCase() + update.status.slice(1)}
                          </span>
                          <span className="ml-2 text-gray-700">{update.message}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600">No incidents reported in the past month.</p>
            )}
          </div>
      </div>
    </div>
  );
};
